import { Injectable } from '@angular/core';
import { merge as _merge } from 'lodash';
import { BehaviorSubject, first, map, of, Subject, switchMap } from 'rxjs';
import { UserState } from '../../../shared';
import * as i0 from "@angular/core";
import * as i1 from "../../../shared";
import * as i2 from "../../../basic-features";
import * as i3 from "@azure/msal-angular";
import * as i4 from "@ngxs/store";
/**
 * @deprecated use v3 version of this component instead
 */
export class TableServiceV2 {
  api;
  errorService;
  msalService;
  store;
  // A cache of all fitler table settings loaded during the applications lifetime, the table ID is the key.
  filterTableSettingsCache = {};
  // A cache of all column table settings loaded during the applications lifetime, the table ID is the key.
  columnSettingsCache = {};
  // A subject that can be used to publish and receive actions through the table service. The table ID defines the table that the action is related to.
  // The action property should be a unique identifier for that table, to define what action has taken place. Additionally, row Data of the affected row can be passed.
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  _actions = new Subject();
  // A subject that can be used to publish and receive column changes through the table service. The table ID defines the table that the change is related to.
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  _columnChanges = new Subject();
  // A subject that can be used to publish and receive cell loading changes through the table service. The table ID defines the table that the change is related to.
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  _cellLoading = new Subject();
  basePath = 'table-settings/v1';
  apiVersion;
  // The key under which the filter table settings are saved in the session storage.
  tableSettingsKey = 'table-settings';
  // Checks if the user is logged in. If the user is not logged in, no information should be exchanged with the backend.
  activeAccount;
  isUserLoggedIn = false;
  constructor(api, errorService, msalService, store) {
    this.api = api;
    this.errorService = errorService;
    this.msalService = msalService;
    this.store = store;
    this.activeAccount = this.msalService.instance?.getActiveAccount();
    // Load all available settings from the session storage.
    this.loadAllFilterTableSettingsFromSessionStorage();
    // Subscribe to the store, to keep track of the login status of the user.
    if (this.store && UserState) {
      this.store.select(UserState.token).subscribe(result => {
        this.isUserLoggedIn = !!result;
      });
    }
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  get actions() {
    return this._actions;
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  publishAction(tableId, action, rowData) {
    // This function can be used to publish actions.
    // Value should be any here, to allow the table to work with any kind of data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this._actions.next({
      tableId,
      action,
      rowData
    });
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  get columnChanges() {
    return this._columnChanges;
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  publishColumnChange(tableId, column, value, rowData) {
    // This function can be used to publish column changes.
    // Value should be any here, to allow the table to work with any kind of data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this._columnChanges.next({
      tableId,
      column,
      value,
      rowData
    });
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  get cellLoading() {
    return this._cellLoading;
  }
  // no-explicit-any is disabled here because the table can work with any kind of data
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  publishCellLoading(tableId, column, rowData, loading) {
    // This function can be used to publish cell loading changes.
    // Value should be any here, to allow the table to work with any kind of data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this._cellLoading.next({
      tableId,
      column,
      rowData,
      loading
    });
  }
  existFilterTableSettingsForTable(tableId) {
    // Checks if filter table settings are present for a specific table.
    return Object.prototype.hasOwnProperty.call(this.filterTableSettingsCache, tableId);
  }
  getFilterTableSettings(tableId) {
    // If there are no existing filter table settings for the given table, returns a behaviour subject with default settings.
    // The behaviour subject can be subscribed to, to be notified of any changes to those settings from any source.
    if (!this.existFilterTableSettingsForTable(tableId)) {
      const defaultSettings = new BehaviorSubject({
        page: 0,
        pageSize: 10,
        columns: {}
      });
      // The behaviour subject is also added to the filter table settings cache.
      this.filterTableSettingsCache[tableId] = defaultSettings;
    }
    // If filter table settings are present for the given table, return a behaviour subject with those.
    // The behaviour subject can be subscribed to, to be notified of any changes to those settings from any source.
    return this.filterTableSettingsCache[tableId];
  }
  updateFilterTableSettings(tableId, sessionTableSettingsValue) {
    // To make an update to filter table settings, first the currently saved value is fetched from the cache.
    const prevSessionTableSettings = this.getFilterTableSettings(tableId).getValue();
    // Then the new filter table settings are merged on top of the existing value. That way the new settings only have to contain the values that actually changed.
    const newSessionTableSettings = {
      ...prevSessionTableSettings,
      ...sessionTableSettingsValue,
      columns: {
        ...prevSessionTableSettings.columns,
        ...sessionTableSettingsValue.columns
      }
    };
    // The filter and sort property is updated manually for each column.
    Object.keys(newSessionTableSettings.columns).forEach(key => {
      if (prevSessionTableSettings?.columns?.[key]?.filter && sessionTableSettingsValue.columns?.[key]?.filter === undefined) {
        newSessionTableSettings.columns[key].filter = prevSessionTableSettings.columns[key].filter;
      }
      if (prevSessionTableSettings?.columns?.[key]?.sort && sessionTableSettingsValue.columns?.[key]?.sort === undefined) {
        newSessionTableSettings.columns[key].sort = prevSessionTableSettings.columns[key].sort;
      }
    });
    // The updated filter table settings are pushed to the behaviour subject and therefor to all subscribers of it.
    this.filterTableSettingsCache[tableId].next(newSessionTableSettings);
    // The updated filter table settings are saved to the session storage.
    this.saveFilterTableSettingsCacheToSessionStorage();
  }
  setFilterTableSettings(tableId, sessionTableSettingsValue) {
    // The new filter table settings are pushed to the behaviour subject and therefor to all subscribers of it.
    this.filterTableSettingsCache[tableId].next(sessionTableSettingsValue);
    // The updated filter table settings are saved to the session storage.
    this.saveFilterTableSettingsCacheToSessionStorage();
  }
  removeColumnTableSettings(tableId) {
    if (this.columnSettingsCache[tableId]) {
      delete this.columnSettingsCache[tableId];
    }
    if (this.activeAccount) {
      this.api.delete(this.basePath + '/' + tableId, {}, this.apiVersion).pipe(first()).subscribe({
        error: err => {
          this.errorService.handleErrorWithPrefix(err, 'UI-LIB');
        }
      });
    }
  }
  updateColumnTableSettings(tableId, columnTableSettingsValue) {
    // To update the column table settings, the existing settings are fetched from the cache or the backend.
    this.fetchSavedColumnTableSettings(tableId).pipe(map(settings => {
      // The new column table settings are applied to the existing ones through a merge. That way the new settings only need to contain the properties that actually changed.
      this.columnSettingsCache[tableId].next(_merge(settings, columnTableSettingsValue));
    }), switchMap(() => {
      // The new column table settings are being saved.
      return this.saveColumnTableSettings(tableId);
    }), first()).subscribe({
      error: err => {
        // If an error occurs, standard error handling is applied.
        this.errorService.handleErrorWithPrefix(err, 'UI-LIB');
      }
    });
  }
  getOrInitializeColumnTableSettings(tableId, initialTableSettings) {
    if (!this.columnSettingsCache[tableId]) {
      // If no column table settings for this table are present in the cache yet, initialize an empty behaviour subject for this table ID.
      this.columnSettingsCache[tableId] = new BehaviorSubject(null);
    }
    if (!initialTableSettings) {
      return this.columnSettingsCache[tableId];
    }
    // Fetch existing column table settings from the cache or the backend.
    this.fetchSavedColumnTableSettings(tableId).pipe(map(columnSettings => {
      // If existing column settings are present and not empty, or if initial table settings were passed, column table settings are returned.
      // The initial column settings are taken as a baseline and overwritten by existing column settings if present.
      if (columnSettings && Object.keys(columnSettings.columns).length || initialTableSettings) {
        return {
          ...(initialTableSettings || {}),
          ...columnSettings,
          columns: {
            ...(initialTableSettings?.columns || {}),
            ...columnSettings?.columns
          }
        };
      } else {
        // If no settings or initial settings are present, just return null.
        return null;
      }
    }), first()).subscribe({
      next: columnSettings => this.columnSettingsCache[tableId].next(columnSettings),
      error: err => {
        this.errorService.handleErrorWithPrefix(err, 'UI-LIB');
      }
    });
    return this.columnSettingsCache[tableId];
  }
  setApiVersion(apiVersion) {
    this.apiVersion = apiVersion ?? this.apiVersion;
    return this;
  }
  isActiveSessionForTable(tableId) {
    const parsedSettings = this.getAndParseTableSettingsFromSessionStorage();
    if (parsedSettings) {
      return Object.keys(parsedSettings).includes(tableId);
    } else {
      return false;
    }
  }
  loadAllFilterTableSettingsFromSessionStorage() {
    const parsedSettings = this.getAndParseTableSettingsFromSessionStorage();
    if (parsedSettings) {
      Object.keys(parsedSettings).forEach(key => {
        this.filterTableSettingsCache[key] = new BehaviorSubject(parsedSettings[key]);
      });
    }
  }
  getAndParseTableSettingsFromSessionStorage() {
    const sessionTableSettings = sessionStorage.getItem(this.tableSettingsKey) ?? '{}';
    try {
      // Parse the data to filter table settings and create an element with the existing settings in the cache for every table ID present.
      const parsedSettings = JSON.parse(sessionTableSettings);
      return parsedSettings;
    } catch (e) {
      // Debug message if the table settings cannot be parsed
      // eslint-disable-next-line no-console
      console.error(`The filter table settings could not be parsed. "${sessionTableSettings}"`);
      return null;
    }
  }
  fetchSavedColumnTableSettings(tableId) {
    // If column settings for this table are present in the cache, return them wrapped in an observable.
    if (this.columnSettingsCache[tableId].getValue()) {
      return of(this.columnSettingsCache[tableId].getValue());
    }
    // If the user is not logged in, return empty settings as an observable instead of attempting to load them from the backend.
    if (!this.activeAccount) {
      return of({
        columns: {}
      });
    }
    // Query the backend for existing settings for this table for this user. Returns an empty object if no settings are present yet.
    return this.api.get(this.basePath + '/' + tableId, {}, this.apiVersion).pipe(map(settings => {
      if (Object.keys(settings).includes('tablePageSize')) {
        const pageSize = settings['tablePageSize'];
        delete settings['tablePageSize'];
        return {
          columns: settings,
          pageSize: pageSize
        };
      }
      return {
        columns: settings
      };
    }));
  }
  saveColumnTableSettings(tableId) {
    const tableSettings = this.columnSettingsCache[tableId].getValue();
    // If the user is not logged in, no settings should be saved to the backend.
    if (!this.activeAccount) {
      return of();
    }
    // Save the settings to the backend.
    return this.api.put(this.basePath + '/' + tableId, {
      ...tableSettings?.columns,
      tablePageSize: tableSettings?.pageSize
    }, {}, this.apiVersion);
  }
  saveFilterTableSettingsCacheToSessionStorage() {
    // Convert the collection of behaviour subjects in the filter table settings cache into a the same object without the behaviour subjects.
    const synchronousSettings = {};
    Object.keys(this.filterTableSettingsCache).forEach(key => {
      synchronousSettings[key] = this.filterTableSettingsCache[key].getValue();
    });
    // Save the settings to the session storage.
    sessionStorage.setItem(this.tableSettingsKey, JSON.stringify(synchronousSettings));
  }
  static ɵfac = function TableServiceV2_Factory(t) {
    return new (t || TableServiceV2)(i0.ɵɵinject(i1.ApiService), i0.ɵɵinject(i2.ErrorHandlerV2Service), i0.ɵɵinject(i3.MsalService), i0.ɵɵinject(i4.Store));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TableServiceV2,
    factory: TableServiceV2.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TableServiceV2, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.ApiService
  }, {
    type: i2.ErrorHandlerV2Service
  }, {
    type: i3.MsalService
  }, {
    type: i4.Store
  }], null);
})();
